import { useEffect, useState } from 'react';

import { BlockData, ImagePreprocessingFunction } from 'types';
import useProject from 'hooks/useProject';
import useImageDataForm from './useImageDataForm';
import fetchImageProcessing from 'api/block/fetchImageProcessing';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_END_POINT from 'constants/apiEndPoints';
import createPreviewImageAsBlob from 'api/block/createPreviewImageAsBlob';
import toast from 'react-hot-toast';

interface UseImageNodeViewerParams {
  id: string;
  blockData: BlockData;
}

const useImageNodeViewer = ({ id, blockData }: UseImageNodeViewerParams) => {
  const [previewImage, setPreviewImage] = useState<{ title: string; image?: any }>();
  const { data: projectData } = useProject();
  const { useFormReturn, nodeDataSave } = useImageDataForm({ id, blockData });

  const { data: preprocessingData, isError } = useQuery({
    queryKey: [API_END_POINT.BLOCK.FETCH_IMAGE_PREPROCESSING],
    queryFn: () =>
      fetchImageProcessing({ framework: projectData!.framework, frameworkVersion: projectData!.frameworkVersion }),
  });
  const { mutate: createPreviewImageMutate } = useMutation({
    mutationFn: createPreviewImageAsBlob,
    onSuccess: (blob) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);

      reader.onload = () => {
        const result = reader.result;

        setPreviewImage((prev) => {
          if (prev) {
            return {
              ...prev,
              image: result,
            };
          }
        });
      };
    },
    onError: () => {
      toast.error('이미지를 불러올 수 없어요.');
    },
  });

  const loadPreviewImage = (title: string, functions: ImagePreprocessingFunction[]) => {
    if (!projectData) return;

    setPreviewImage({ title });

    createPreviewImageMutate({
      framework: projectData.framework,
      frameworkVersion: projectData.frameworkVersion,
      functions,
    });
  };

  const handlePreviewClose = () => {
    setPreviewImage(undefined);
  };

  useEffect(() => {
    if (isError) {
      toast.error('잠시후 다시 시도해주세요.');
    }
  }, [isError]);

  return {
    previewImage,
    useFormReturn,
    isLoading: !preprocessingData || !projectData,
    preprocessingData,
    nodeDataSave,
    loadPreviewImage,
    onPreviewClose: handlePreviewClose,
  };
};

export default useImageNodeViewer;
